/**
 * Step 6.3 -- the other players.
 *
 * A peer's character arrives as a handful of strings in the room state, and that is
 * the whole point: the appearance is a record of choices, every client already has the
 * pack, so rendering someone else costs no asset transfer beyond whatever slot bundles
 * they happen to use that this client has not loaded yet.
 *
 * Peers are assembled once on join and then only moved. The server owns position, so
 * this reads `x`/`y`/`z`/`yaw` and never writes them.
 */
import { decodeAppearance, decodeTints, type StanceClass } from '@dungeon-builder/shared';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Scene } from '@babylonjs/core/scene';

import { spawnAvatar, type Avatar } from './localAvatar.ts';
import type { BattleStateView, PlayerView } from '../net/battleConnection.ts';

interface Remote {
  avatar: Avatar | null;
  /** Set while the character is still assembling, so a second update does not race. */
  pending: boolean;
  /** What was rendered, so only real changes cause a rebuild. */
  signature: string;
}

function signatureOf(player: PlayerView): string {
  return [player.gender, player.stance, player.packId, player.appearance, player.tints, player.colourway].join('|');
}

export class RemoteAvatars {
  private readonly remotes = new Map<string, Remote>();

  constructor(
    private readonly scene: Scene,
    /** This client's own session, which renders as the local avatar instead. */
    private readonly selfSessionId: () => string | undefined,
    /** Server coordinates are level-relative until the server loads levels itself. */
    private readonly worldFrom: (x: number, y: number, z: number) => Vector3,
  ) {}

  /** Reconciles the scene against the room state. Safe to call every state change. */
  sync(state: BattleStateView): void {
    const self = this.selfSessionId();
    const seen = new Set<string>();

    for (const [sessionId, player] of state.players) {
      if (sessionId === self) continue;
      seen.add(sessionId);

      const existing = this.remotes.get(sessionId);
      const signature = signatureOf(player);

      if (!existing) {
        const remote: Remote = { avatar: null, pending: true, signature };
        this.remotes.set(sessionId, remote);
        void this.assemble(sessionId, player, remote);
      } else if (existing.signature !== signature && !existing.pending) {
        // Appearance changes only between runs, so this is rare -- but a peer
        // reconnecting with a different character must not keep the old body.
        existing.signature = signature;
        existing.pending = true;
        existing.avatar?.dispose();
        existing.avatar = null;
        void this.assemble(sessionId, player, existing);
      }

      const avatar = existing?.avatar ?? this.remotes.get(sessionId)?.avatar;
      if (avatar) {
        avatar.root.position.copyFrom(this.worldFrom(player.x, player.y, player.z));
        avatar.root.rotation.y = player.yaw;
      }
    }

    for (const [sessionId, remote] of this.remotes) {
      if (seen.has(sessionId)) continue;
      remote.avatar?.dispose();
      this.remotes.delete(sessionId);
    }
  }

  private async assemble(sessionId: string, player: PlayerView, remote: Remote): Promise<void> {
    try {
      const avatar = await spawnAvatar(this.scene, {
        gender: player.gender === 'female' ? 'female' : 'male',
        stance: (player.stance as StanceClass) || 'swordsman',
        position: this.worldFrom(player.x, player.y, player.z),
        colourway: player.colourway || undefined,
        tints: decodeTints(player.tints),
        variantIds: decodeAppearance(player.appearance),
      });
      // The peer may have left while their body was loading.
      if (!this.remotes.has(sessionId)) {
        avatar.dispose();
        return;
      }
      remote.avatar = avatar;
      console.log(`[character] peer ${sessionId} rendered as a ${player.gender} ${player.stance}`);
    } catch (error) {
      console.warn(`[character] could not render peer ${sessionId}`, error);
    } finally {
      remote.pending = false;
    }
  }

  get count(): number {
    return this.remotes.size;
  }

  dispose(): void {
    for (const remote of this.remotes.values()) remote.avatar?.dispose();
    this.remotes.clear();
  }
}
